<!--
  Notes and messages that carry media, newest first: one tab of the library in every layout.
  Notes hold frozen references: a thumbnail opens exactly the items the note was written with,
  with the current set offered when it differs. Several of a note's attachments can be made into a
  set right here. Message attachments stay in the conversation until someone saves them to the
  patient's media.
-->
<template>
  <div class="md-notes">
    <p v-if="loading" class="md-muted">Loading…</p>
    <p v-else-if="!sources.length" class="md-muted">No notes or messages with media for this patient.</p>
    <article v-for="s in sources" :key="key(s)" class="md-card md-notes-item" :class="[s.source_type, { on: activeKey === key(s) }]">
      <header class="md-row">
        <span class="md-notes-type">{{ s.source_type === 'message' ? '✉ ' : '📝 ' }}{{ s.type }}</span>
        <span class="md-grow" />
        <span class="md-small md-muted">{{ s.provsignature }} · {{ fmt(s.postDate) }}</span>
      </header>
      <p class="md-notes-text">{{ s.pnotes }}</p>

      <div v-if="s.media.length" class="md-notes-attach">
        <div class="md-row md-small">
          <span class="md-muted">{{ s.media.length }} attached{{ s.set_name ? ` · from the set “${s.set_name}”` : '' }}</span>
          <span v-if="s.set_changed" class="md-pill staff">set changed since</span>
          <span class="md-grow" />
          <button v-if="store.canManage && s.media.length > 1" class="md-link md-small" @click="toggleSelect(s)">{{ selecting === key(s) ? 'Cancel' : 'Make a set…' }}</button>
        </div>
        <div class="md-notes-strip">
          <button v-for="(it, i) in s.media" :key="it.id" class="md-notes-thumb" :class="{ picked: selecting === key(s) && picked.includes(it.id), removed: it.removed_at }" @click="clickThumb(s, i, it)">
            <MediaThumb :item="it" ratio="1 / 1" />
            <span v-if="selecting === key(s)" class="md-notes-check">{{ picked.includes(it.id) ? '✓' : '' }}</span>
          </button>
        </div>
        <div v-if="selecting === key(s)" class="md-row md-notes-mkset">
          <input v-model="setName" class="md-input md-grow" placeholder="Name for the new set" maxlength="150" />
          <button class="md-btn sm" :disabled="picked.length < 1 || !setName.trim()" @click="makeSet">Create set ({{ picked.length }})</button>
        </div>
      </div>

      <div v-if="s.message_files && s.message_files.length" class="md-notes-attach">
        <div class="md-small md-muted">Attached to this message (stays in the conversation)</div>
        <div class="md-notes-msgfiles">
          <div v-for="f in s.message_files" :key="f.stored_file_id" class="md-notes-msgfile">
            <img :src="f.src" alt="" />
            <button v-if="!f.saved_media_id && store.canManage" class="md-btn ghost sm" @click="saveFile(s, f)">Save to patient media</button>
            <span v-else-if="f.saved_media_id" class="md-pill visible">Saved to media</span>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useMediaStore } from '@/stores/mediaStore';
import { listReferencingSources } from '@/api/mediaApi';
import MediaThumb from './MediaThumb.vue';

defineProps({ activeKey: { type: String, default: '' } });
const emit = defineEmits(['open-note']);
const store = useMediaStore();
const sources = ref([]);
const loading = ref(false);
const selecting = ref(null);
const picked = ref([]);
const setName = ref('');

async function load() {
  if (store.inLibrary) { sources.value = []; return; }
  loading.value = true;
  try { sources.value = await listReferencingSources(store.patientUid); } catch (e) { sources.value = []; store.say(e.message, 'error'); }
  loading.value = false;
}

const key = (s) => `${s.source_type}:${s.source_id}`;

function clickThumb(s, i, it) {
  if (selecting.value !== key(s)) { emit('open-note', key(s), i); return; }
  const at = picked.value.indexOf(it.id);
  if (at >= 0) picked.value.splice(at, 1); else picked.value.push(it.id);
}
function toggleSelect(s) {
  if (selecting.value === key(s)) { selecting.value = null; return; }
  selecting.value = key(s);
  picked.value = s.media.filter((it) => !it.removed_at).map((it) => it.id);
  setName.value = '';
}
async function makeSet() {
  const out = await store.run((api) => api.saveMediaSet({ patient_unique_id: store.patientUid, name: setName.value, add_item_ids: [...picked.value] }), {
    success: (r) => `Set “${r.name}” created from the note’s attachments. The note keeps its own copy of the list.`, bump: true,
  });
  if (out) { selecting.value = null; picked.value = []; }
}
async function saveFile(s, f) {
  await store.run((api) => api.saveMessageMediaToPatient(s.source_id, f.stored_file_id, store.patientUid), { success: 'Saved to the patient’s media. The message keeps its attachment.', bump: true });
}
const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');

watch(() => [store.patientUid, store.version], load, { immediate: true });
</script>

<style scoped>
.md-notes { display:flex; flex-direction:column; gap:12px; }
.md-notes-item { padding:14px; }
.md-notes-item.message { border-left:4px solid var(--blue); }
.md-notes-item.on { border-color:var(--teal); box-shadow:0 0 0 2px #bfe0dd; }
.md-notes-type { font-weight:700; }
.md-notes-text { margin:8px 0; white-space:pre-wrap; }
.md-notes-attach { border-top:1px solid var(--line); padding-top:8px; margin-top:8px; }
.md-notes-strip { display:flex; gap:8px; overflow-x:auto; padding:8px 0 2px; }
.md-notes-thumb { position:relative; flex:0 0 92px; border:2px solid transparent; border-radius:8px; overflow:hidden; padding:0; cursor:pointer; background:none; }
.md-notes-thumb.picked { border-color:var(--teal); }
.md-notes-thumb.removed { opacity:.55; }
.md-notes-check { position:absolute; top:4px; left:4px; width:22px; height:22px; border-radius:5px; background:rgba(0,0,0,.45); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
.md-notes-thumb.picked .md-notes-check { background:var(--teal); }
.md-notes-mkset { margin-top:8px; }
.md-notes-msgfiles { display:flex; gap:12px; flex-wrap:wrap; padding-top:8px; }
.md-notes-msgfile { display:flex; flex-direction:column; gap:6px; width:150px; }
.md-notes-msgfile img { width:150px; height:112px; object-fit:cover; border-radius:8px; }
</style>
